import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { orgApi, OrgInfo } from "../services/orgApi";
import { READ_ONLY_EVENT, getSupportView } from "../services/supportView";

// The signed-in user's organisation and role, shared by everything that asks
// (top bar, settings, read-only checks). One store, so a change shows
// everywhere at once.
//
// A role can change while the app is open (an editor makes you a viewer, or
// back), so it's re-read when you return to the tab, every few minutes, and
// right after the backend refuses something as editors-only.

const RECHECK_MS = 5 * 60 * 1000;

type State = { uid: string | null; info: OrgInfo | null; error: boolean };
let state: State = { uid: null, info: null, error: false };
let inflight: Promise<void> | null = null;
const listeners = new Set<(s: State) => void>();

const publish = (next: State) => {
  state = next;
  listeners.forEach((l) => l(state));
};

// The last answer is kept in this browser, so the menu shows your
// organisation and role at once on the next page load while it's re-checked.
// Display only: the backend decides what you may actually do.
const cacheKey = (uid: string) => `telcofy.org.${uid}.${getSupportView()?.id ?? "self"}`;
const readCached = (uid: string): OrgInfo | null => {
  try {
    const raw = localStorage.getItem(cacheKey(uid));
    return raw ? (JSON.parse(raw) as OrgInfo) : null;
  } catch {
    return null;
  }
};
const writeCached = (uid: string, info: OrgInfo) => {
  try {
    localStorage.setItem(cacheKey(uid), JSON.stringify(info));
  } catch {}
};

function fetchOrg(uid: string) {
  if (inflight && state.uid === uid) return inflight;
  inflight = orgApi
    .getOrg()
    .then((info) => {
      if (state.uid === uid) {
        publish({ uid, info, error: false });
        writeCached(uid, info);
      }
    })
    .catch(() => {
      if (state.uid === uid) publish({ ...state, error: true });
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useOrg() {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid ?? null;
  const [s, setS] = useState<State>(state);

  useEffect(() => {
    listeners.add(setS);
    if (state.uid !== uid) {
      inflight = null;
      publish({ uid, info: uid ? readCached(uid) : null, error: false });
      if (uid) fetchOrg(uid); // confirm the remembered answer (or get the first one)
    } else if (uid && !state.info && !inflight) fetchOrg(uid);
    else setS(state);
    return () => {
      listeners.delete(setS);
    };
  }, [uid]);

  // Re-read on return to the tab, on a timer, and after an editors-only refusal.
  useEffect(() => {
    if (!uid) return;
    const recheck = () => {
      if (document.visibilityState === "visible") fetchOrg(uid);
    };
    const onRefused = () => fetchOrg(uid);
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    window.addEventListener(READ_ONLY_EVENT, onRefused);
    const timer = setInterval(recheck, RECHECK_MS);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
      window.removeEventListener(READ_ONLY_EVENT, onRefused);
      clearInterval(timer);
    };
  }, [uid]);

  const info = s.uid === uid ? s.info : null;
  const role = info?.organisation ? info.me?.role ?? null : null;
  return {
    info,
    organisation: info?.organisation ?? null,
    role,
    isEditor: role === "editor",
    error: s.uid === uid && s.error,
    refresh: () => (uid ? fetchOrg(uid) : Promise.resolve()),
  };
}
